/**
 * The academy's payment audit trail — every action anybody has taken, permanently.
 *
 * WHAT THIS SCREEN IS FOR. Not "recent activity". It is the answer to a question asked months
 * later by somebody who was not there: who refunded this, on what date, for what stated
 * reason, and what happened to the learner's access. The rows are never edited and never
 * deleted, so the only way this screen can lie is by rendering a fact as a different fact —
 * which is what most of the code below is about.
 *
 * FIVE THINGS THE PAYLOAD SAYS THAT A NAIVE TABLE WOULD GET WRONG:
 *
 *   `outcome: 'requested'` is NOT done. A Stripe refund starts as a request and an FPX refund
 *   can sit there for weeks. It gets its own tone and its own sentence, and the word
 *   "refunded" never appears against it.
 *
 *   `outcome: 'partially_succeeded'` means SOMETHING WAS LEFT IN PLACE — a course the learner
 *   holds independently, or an item that was offered for revocation and not confirmed. Its own
 *   tone, and a sentence, because "partly" with no explanation is worse than either extreme.
 *
 *   `settled_at` is null while only the request row exists. A blank cell there reads as
 *   instantaneous. See `When`, which also explains why the gap is only stated for a request:
 *   `audit.timeline` only ever sets `settled_at` from a SECOND row for the same operation, so
 *   a one-row action that succeeded outright has a null there and nothing is outstanding.
 *
 *   `amount_minor: null` with `no_money_moved: true` is the affirmative "none" — not zero, not
 *   unknown. Cancelling access moves no money, and RM 0.00 there would read as a failed
 *   refund.
 *
 *   `needs_action.blocked_upstream_orders` and `revoke_failed_orders` can be `-1`, meaning the
 *   order table could not be read. See `Count`.
 *
 * THE CSV EXPORTS MORE THAN THIS SCREEN SHOWS, and the screen says so next to the link. That
 * asymmetry is the server's: `format=csv` honours actor/action/outcome/since/until and ignores
 * `order`, while the JSON mode does the reverse. A download that silently contains the whole
 * academy when the operator was looking at one order is a data-export surprise, and the fix is
 * a sentence rather than a pretence that the filters match.
 *
 * NOTHING HERE IS A WRITE, so there is no `may()` check and no affordance to gate. `controls`
 * still arrives with the response, and a refusal is shown for the same reason it is shown on
 * the list: an operator should learn they are read-only before they go looking for a button.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Card,
  DataTable,
  EmptyState,
  Kpi,
  KpiRow,
  Spinner,
  type Column,
} from '../../shared/ui';
import { formatMoney } from '../../shared/lib/pricing';
import {
  auditCsvUrl,
  auditKey,
  countOf,
  explainOpsFailure,
  loadAudit,
  OPS_COPY,
  writeGate,
  type WireAuditEntry,
  type WireNeedsAction,
} from '../api/paymentOps';
import { PageHeader } from '../AdminShell';
import '../payments/payments.css';

/** The academy-wide trail. `auditKey('')` is the same key `loadAudit()` with no order fills. */
const ACADEMY_WIDE = '';

const ACTION_LABELS: Record<string, string> = {
  refund: 'Refund, access removed',
  refund_keep_access: 'Refund, access kept',
  cancel_access: 'Access cancelled, no refund',
  regrant_access: 'Access restored',
  cancel_free_enrolment: 'Free enrolment cancelled',
  record_free_grant: 'Free access recorded',
  certificate_revoke: 'Certificate revoked',
  certificate_retain: 'Certificate kept',
  promo_release: 'Promotion redemption released',
  obligation_settle: 'Obligation settled outside Stripe',
  obligation_write_off: 'Obligation written off',
  dispute_note: 'Dispute note',
  mandate_use: 'Acted under a mandate',
  cross_tenant_read: 'Order read by a platform operator',
};

const OUTCOME_LABELS: Record<string, string> = {
  requested: 'Requested',
  succeeded: 'Succeeded',
  failed: 'Failed',
  partially_succeeded: 'Partly succeeded',
};

/**
 * Four outcomes, four tones. `requested` is `info` and NOT `success`: it is the state in which
 * money has been asked for and not confirmed, and green there is the single most expensive
 * misread available on this screen.
 */
const OUTCOME_TONES: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  requested: 'info',
  succeeded: 'success',
  failed: 'danger',
  partially_succeeded: 'warning',
};

/** Said under the badge, because neither of these two outcomes explains itself. */
const OUTCOME_NOTES: Record<string, string> = {
  requested: OPS_COPY.requested,
  partially_succeeded:
    'Part of this action did not happen. Something was left in place — read the detail and the items below before assuming the rest went through.',
};

const humanize = (value: string) =>
  value ? value.replaceAll('_', ' ').replace(/^./, (first) => first.toUpperCase()) : '—';

const stamp = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/**
 * A count that may be unknown, rendered as unknown.
 *
 * `-1` from `obligations._count_orders` means the order table could not be read. Printing
 * `-1` is nonsense and coercing it to `0` reports "nothing is stuck" on the evidence that we
 * could not look — the server's own comment calls that the same lie as a price reading "free"
 * because the route 404'd. `countOf` is the seam's reader for it and this component is the
 * only place that renders the result.
 */
function Count({ raw }: { raw: number | null | undefined }) {
  const state = countOf(raw);
  if (!state.known) return <span className="ops-audit__unknown">{OPS_COPY.unknownCount}</span>;
  return <>{state.value}</>;
}

/**
 * When it happened, and when it was confirmed — two lines whenever those are two moments.
 *
 * A single timestamp on a refund reads as "done then". The confirmation line is only claimed
 * missing for `requested`, because `settled_at` is populated from a second audit row and an
 * action that recorded its outcome in one row has nothing outstanding to wait for.
 */
function When({ entry }: { entry: WireAuditEntry }) {
  return (
    <span className="ops-audit__when">
      {stamp(entry.occurred_at)}
      {entry.settled_at ? (
        <small className="admin-sub">confirmed {stamp(entry.settled_at)}</small>
      ) : entry.outcome === 'requested' ? (
        <small className="admin-sub">not yet confirmed</small>
      ) : null}
    </span>
  );
}

/**
 * The amount, or the affirmative absence of one.
 *
 * Three different renderings for three different facts, which is the whole rule: "no money
 * moved" is a statement, "not recorded" is a gap in the row, and an amount is an amount.
 * Collapsing any two of them into RM 0.00 or an em dash loses the distinction that makes the
 * trail worth keeping.
 */
function Amount({ entry }: { entry: WireAuditEntry }) {
  if (entry.no_money_moved) return <span className="admin-muted">No money moved</span>;
  if (entry.amount_minor === null) return <span className="admin-muted">Not recorded</span>;
  if (!entry.currency) {
    return (
      <span>
        {formatMoney(entry.amount_minor, entry.currency)}
        <small className="admin-sub">currency not recorded</small>
      </span>
    );
  }
  return <>{formatMoney(entry.amount_minor, entry.currency)}</>;
}

/** Which courses, pathways and certificates this action touched. */
function Items({ entry }: { entry: WireAuditEntry }) {
  const grants = entry.affected_grants ?? [];
  const certificates = entry.certificate_decisions ?? [];
  if (grants.length === 0 && certificates.length === 0) {
    return <span className="admin-muted">—</span>;
  }
  return (
    <span className="ops-audit__items">
      {grants.map((grant) => (
        <Badge
          key={`grant:${grant.target_kind}:${grant.target_key}`}
          tone={grant.decision === 'revoked' ? 'danger' : 'success'}
        >
          {grant.target_key}
        </Badge>
      ))}
      {certificates.map((certificate) => (
        <Badge
          key={`cert:${certificate.identifier}`}
          tone={certificate.decision === 'revoked' ? 'danger' : 'neutral'}
        >
          certificate {certificate.identifier}
        </Badge>
      ))}
    </span>
  );
}

/**
 * The obligations total's currency, taken from the trail.
 *
 * `needs_action` carries `open_obligation_total_minor` and NO CURRENCY CODE, so there is
 * nothing in that block to format it with. Rather than assume one, the code is read off the
 * first entry that states it — every order in one academy settles in that academy's own
 * currency — and when the trail is empty the total is not shown at all. Named in the report
 * back as a payload gap rather than papered over.
 */
const currencyOf = (entries: readonly WireAuditEntry[]): string =>
  entries.find((entry) => entry.currency)?.currency ?? '';

function NeedsAction({
  needs,
  currency,
}: {
  needs: WireNeedsAction;
  currency: string;
}) {
  return (
    <KpiRow>
      <Kpi
        label="Open obligations"
        value={<Count raw={needs.open_obligations} />}
        hint={
          currency
            ? `${formatMoney(needs.open_obligation_total_minor, currency)} still owed to learners`
            : 'Owed to learners. The total is not shown because the payload states no currency for it.'
        }
      />
      <Kpi
        label="Open disputes"
        value={<Count raw={needs.open_disputes} />}
        hint="Answer these in Stripe. Refunding a disputed charge pays the learner twice."
      />
      <Kpi
        label="Refunds pending past their window"
        value={<Count raw={needs.refund_pending_past_window} />}
        hint="Asked for, and past the point the payment method said it would take."
      />
      <Kpi
        label="Orders blocked upstream"
        value={<Count raw={needs.blocked_upstream_orders} />}
        hint="Paid, and access held back because a contract check failed."
      />
      <Kpi
        label="Orders whose revoke failed"
        value={<Count raw={needs.revoke_failed_orders} />}
        hint="Refunded or cancelled, and the learner may still have access."
      />
    </KpiRow>
  );
}

export default function PaymentAudit() {
  const audit = useQuery({
    queryKey: auditKey(ACADEMY_WIDE),
    queryFn: () => loadAudit(),
    retry: false,
  });

  const data = audit.data;
  const entries = data?.entries ?? [];
  const gate = data ? writeGate(data.controls) : null;
  const failure = audit.isError ? explainOpsFailure(audit.error) : null;
  const currency = currencyOf(entries);

  const columns: Column<WireAuditEntry>[] = [
    { key: 'when', header: 'When', render: (entry) => <When entry={entry} /> },
    {
      // WITHOUT THIS COLUMN THE TRAIL COULD NOT ANSWER "what happened to ord_...".
      // It showed what was done, by whom, for how much and why, and not what it was done to.
      key: 'order',
      header: 'Order',
      mono: true,
      render: (entry) =>
        entry.order_ref ? (
          <Link to={`/admin/payments/${encodeURIComponent(entry.order_ref)}`}>
            {entry.order_ref}
          </Link>
        ) : (
          // An affirmative none, not an unknown: some actions genuinely have no order behind
          // them, and a free-enrolment cancellation is the one that reaches this screen.
          <span className="admin-muted">no order</span>
        ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (entry) => (
        <>
          {ACTION_LABELS[entry.action] ?? humanize(entry.action)}
          <div className="admin__sub">{entry.operation_id}</div>
        </>
      ),
    },
    {
      key: 'actor',
      header: 'Who',
      render: (entry) => (
        <>
          {entry.actor_username || <span className="admin-muted">Not recorded</span>}
          <div className="admin__badges">
            {entry.actor_is_superadmin && <Badge tone="info">Platform operator</Badge>}
            {/* `acted_under_mandate` is null, not '', when there is none. A recorded mandate
                is the superadmin-acting-for-this-academy case and it has to be visible: the
                id is the part somebody can go and check. */}
            {entry.acted_under_mandate && (
              <Badge tone="warning">Under mandate {entry.acted_under_mandate}</Badge>
            )}
          </div>
        </>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (entry) => (
        <>
          <Badge tone={OUTCOME_TONES[entry.outcome] ?? 'neutral'}>
            {OUTCOME_LABELS[entry.outcome] ?? humanize(entry.outcome)}
          </Badge>
          {OUTCOME_NOTES[entry.outcome] && (
            <span className="ops-audit__note">{OUTCOME_NOTES[entry.outcome]}</span>
          )}
          {/* The server's own detail, unedited. On a failure it is the only line that says
              what went wrong. */}
          {entry.outcome_detail && (
            <span className="ops-audit__note">{entry.outcome_detail}</span>
          )}
        </>
      ),
    },
    { key: 'amount', header: 'Amount', numeric: true, render: (entry) => <Amount entry={entry} /> },
    {
      key: 'reason',
      header: 'Reason',
      render: (entry) =>
        entry.reason || <span className="admin-muted">None recorded</span>,
    },
    { key: 'items', header: 'Affected', render: (entry) => <Items entry={entry} /> },
  ];

  return (
    <>
      <PageHeader
        title="Payment audit trail"
        description="Every action anybody has taken on this academy's orders. Each row is permanent — nothing here can be edited or removed, including by us."
      />

      {gate && gate.kind !== 'writable' && (
        <Banner tone="info">
          <strong>{gate.reason}</strong> This trail is readable either way.
        </Banner>
      )}

      <div className="ops-audit">
        {audit.isPending ? (
          <Spinner label="Loading this academy's payment audit trail…" />
        ) : failure ? (
          <Banner tone="danger">
            <strong>{failure.message}</strong>
            {failure.nextStep && <p className="admin-note">{failure.nextStep}</p>}
          </Banner>
        ) : (
          <>
            {data?.needs_action && (
              <NeedsAction needs={data.needs_action} currency={currency} />
            )}

            <div className="ops-audit__export">
              {/*
                A plain link, not a fetch. A GET needs no CSRF token and the browser already
                holds the session, so the download is the browser's and a failure shows as a
                page rather than as a silently empty file.
              */}
              <a href={auditCsvUrl()} download>
                Export the audit trail (CSV)
              </a>
              <p>
                The export contains this academy&rsquo;s <strong>entire</strong> trail. It is not
                narrowed by anything on this screen — the CSV mode of that endpoint ignores the
                order filter, so a download taken from one order&rsquo;s page still holds every
                order&rsquo;s rows.
              </p>
            </div>

            {entries.length === 0 ? (
              <EmptyState
                title="Nothing has been done to this academy's orders yet"
                body="A row appears here the first time somebody refunds an order, removes or restores access, or settles a refund outside Stripe."
              />
            ) : (
              <Card>
                <DataTable
                  columns={columns}
                  rows={entries}
                  /* `operation_id` alone is not unique: one operation can write a request row
                     and a settlement row under two actions, and `timeline` collapses per
                     `operation_id:action`. Keyed the same way, so React's list identity
                     matches the server's. */
                  rowKey={(entry) => `${entry.operation_id}:${entry.action}`}
                  /* Newest first is the server's order (`timeline` sorts descending) and it is
                     not re-sorted here — a second sort is a second definition of "when", and
                     `occurred_at` and `settled_at` disagree about it on purpose. */
                  empty={null}
                />
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}
